import {
  MAutocomplete,
  MButton,
  MCheckbox,
  ResponsiveFormContainer,
} from "@app/components";
import DateRangeForm from "@app/pages/report/components/subForms/dateRangeForm";
import DateRangeWeek from "@app/pages/report/components/subForms/dateRangeWeek";
import { MODES_OPTIONS } from "@app/types/generics.types";
import {
  Box,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  useInstallation,
  useSamplingReason,
} from "@services/servicesGenericsTables";
import dayjs from "dayjs";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSample } from "../../utils/useSample";
import MUploadFile from "@app/components/MUploadFile";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
};

interface KeeperModalProps {
  open: boolean;
  onClose: () => void;
}

export interface KeeperFormValues {
  type_action: { label: string; id: string };
  filterReport: string[] | undefined;
  installation: { codigo: string; descripcion: string } | undefined;
  searchType: "dateRange" | "dateRangeWeek";
  evo_start_date: dayjs.Dayjs | undefined;
  evo_end_date: dayjs.Dayjs | undefined;
  year_pickup: number | undefined;
  start_week_pickup: number | undefined;
  end_week_pickup: number | undefined;
  motivo_muestreo: { codigo: string; descripcion: string } | undefined;
  importFile?: File;
}

const TypeAction = {
  EXPORT: "export",
  IMPORT: "import",
};

const TYPE_ACTION = [
  { label: "Exportar", id: TypeAction.EXPORT },
  { label: "Importar", id: TypeAction.IMPORT },
];
const OPTIONS_CHECKS = [
  {
    label: "Con Referencia aplicación informática",
    value: "check_referent_app_info",
  },
  {
    label: "Con Referente Cliente",
    value: "check_referent_cliente",
  },
  {
    label: "Sólo LID",
    value: "check_solid",
  },
];

const KeeperModal: React.FC<KeeperModalProps> = ({ open, onClose }) => {
  const { installationList } = useInstallation();
  const { exportKeeper, loadingExport, importKeeper } = useSample();
  const { samplingReasonList } = useSamplingReason();

  const today = dayjs();
  const form = useForm<KeeperFormValues>({
    mode: "onSubmit",
    shouldFocusError: true,
    defaultValues: {
      type_action: TYPE_ACTION[0],
      filterReport: [],
      installation: undefined,
      searchType: "dateRange",
      evo_start_date: today,
      evo_end_date: today,
      year_pickup: undefined,
      start_week_pickup: undefined,
      end_week_pickup: undefined,
      motivo_muestreo: undefined,
    },
  });

  const searchType = form.watch("searchType") || "dateRange";
  const watchTypeAction = form.watch("type_action")?.id;

  const submit = async (data: KeeperFormValues) => {
    if (watchTypeAction === TypeAction.EXPORT) {
      await exportKeeper(data);
    } else if (watchTypeAction === TypeAction.IMPORT && data.importFile) {
      await importKeeper(data.importFile);
    }
  };

  const changeTypeDate = (type: string) => {
    if (type === "dateRange") {
      form.setValue("year_pickup", undefined);
      form.setValue("start_week_pickup", undefined);
      form.setValue("end_week_pickup", undefined);
      form.setValue("evo_start_date", today);
      form.setValue("evo_end_date", today);
    } else {
      form.setValue("evo_start_date", undefined);
      form.setValue("evo_end_date", undefined);
    }
    if (type === "dateRangeWeek" || type === "dateRange")
      form.setValue("searchType", type);
  };

  return (
    <Modal
      open={open}
      onClose={!loadingExport ? onClose : undefined}
      aria-labelledby="keeper modal"
    >
      <Box sx={style}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(submit)}>
            <Typography
              id="modal-title"
              variant="h5"
              component="h2"
              sx={{ mb: 3, fontWeight: "bold" }}
            >
              {"KEEPER"}
            </Typography>

            <ResponsiveFormContainer columns={3} mb={2}>
              <MAutocomplete
                name="type_action"
                options={TYPE_ACTION}
                label="Seleccione una acción"
                getOptionLabel={(option) => `${option.label}`}
              />
            </ResponsiveFormContainer>

            {watchTypeAction === TypeAction.EXPORT && (
              <>
                <ResponsiveFormContainer columns={1} mb={2}>
                  <MCheckbox
                    key={JSON.stringify(form.getValues("filterReport"))}
                    name="filterReport"
                    options={OPTIONS_CHECKS}
                    multiple={true}
                    orientation="horizontal"
                    columns={3}
                  />
                </ResponsiveFormContainer>

                <ResponsiveFormContainer columns={3} mb={2}>
                  <MAutocomplete
                    name="installation"
                    options={installationList}
                    label="Instalación"
                    getOptionLabel={(option) =>
                      `${option.codigo} - ${option.descripcion}`
                    }
                  />
                  <MAutocomplete
                    key={JSON.stringify(form.getValues("motivo_muestreo"))}
                    name="motivo_muestreo"
                    options={samplingReasonList}
                    label="Seleccione un motivo"
                    getOptionLabel={(option) =>
                      `${option.codigo} - ${option.descripcion}`
                    }
                  />
                </ResponsiveFormContainer>

                <Controller
                  name="searchType"
                  control={form.control}
                  defaultValue={searchType}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        changeTypeDate(e.target.value);
                      }}
                      row
                    >
                      <FormControlLabel
                        value="dateRange"
                        control={<Radio />}
                        label="Rango de fechas"
                      />
                      <FormControlLabel
                        value="dateRangeWeek"
                        control={<Radio />}
                        label="Rango de semana"
                      />
                    </RadioGroup>
                  )}
                />

                {form.watch("searchType") === "dateRange" && (
                  <DateRangeForm mode={MODES_OPTIONS.NEW} key="dateRange" />
                )}
                {form.watch("searchType") === "dateRangeWeek" && (
                  <DateRangeWeek mode={MODES_OPTIONS.NEW} key="dateRangeWeek" />
                )}
              </>
            )}

            {watchTypeAction === TypeAction.IMPORT && (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    INSTRUCCIONES DE IMPORTACIÓN DE FICHERO KEEPER A FORMATO
                    "CSV":
                  </b>
                </Typography>
                <ol>
                  <li>
                    Colocar el fichero KEEPER con extensión "DAT" en el
                    directorio:
                    <br />
                    <i>
                      C:\Programacion\Medidas Ambientales\mambo_tablas\keeper
                    </i>
                  </li>
                  <li>Usar el botón "IMPORTAR".</li>
                  <li>
                    El resultado se obtiene en el fichero "históricos.csv" en la
                    ruta:
                    <br />
                    <i>C:\Programacion\Medidas Ambientales\mambo_tablas</i>
                  </li>
                  <li>
                    El fichero "CSV" generado debe importarse directamente en la
                    BBDD.
                  </li>
                </ol>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Seleccione el archivo a importar:
                  </Typography>
                  <MUploadFile
                    acceptFile=".dat"
                    name="importFile"
                    disabled={loadingExport}
                    onFileChange={(file) => {
                      if (file instanceof File) {
                        form.setValue("importFile", file);
                      }
                    }}
                  />
                </Box>
              </>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <MButton
                variant="contained"
                color="primary"
                text="Guardar"
                type="submit"
                disabled={loadingExport}
              />
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ ml: 2 }}
                text="Cancelar"
                onClick={onClose}
                disabled={loadingExport}
              />
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default KeeperModal;
